/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from '../../../../../../core/components';
import { Component } from '@angular/core';
import { DropdownModel, ErrorResponseModel } from '../../../../../../core/models';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { IpGroupApiService, NetworkingApiService } from '../../../../shared/http';
import { LogService, NotifyService, TransactionService, UtilsService } from '../../../../../../core/services';
import { NetworkingService } from '../../../../shared/services/components';
import { catchError, concatMap, mergeMap, takeUntil } from 'rxjs/operators';
import { forkJoin, from, Observable, of } from 'rxjs';

@Component({
    selector: 'app-break-bond',
    templateUrl: './break-bond.component.html',
    styleUrls: ['./break-bond.component.scss']
})
export class BreakBondComponent extends BaseComponent {
    /**
     * Step position.
     */
    public activeStep: number = 1;

    /**
     * FormGroup.
     */
    public formGroup: FormGroup;

    /**
     * Store for dropdown content.
     */
    public dropdownContent: DropdownModel[] = [];

    /**
     * Store if the bond has groups
     */
    public hasGroups: boolean = true;

    /**
     * Store if the bond has VLANs
     */
    public hasVLANs: boolean = true;

    /**
     * Store loaded state.
     */
    public isLoaded: boolean = false;

    /**
     * Store interfaces that aren't VLANs or a bond.
     * @private
     */
    private nonVLANInterfaces: any[];

    /**
     * Store the bond id.
     * @private
     */
    private readonly bondId: any;

    /**
     * Store the formatted VLANs.
     * @private
     */
    private formattedVLANs: any[] = [];

    /**
     * Store the VLANs associated to the bond.
     * @private
     */
    private bondVLANs: any[] = [];

    /**
     * Store the IP Groups.
     * @private
     */
    private ipGroups: any[] = [];

    /**
     * Store the formatted IP Groups associated to the bond.
     * @private
     */
    private formattedIPGroups: any[] = [];

    constructor(
        private activatedRoute: ActivatedRoute,
        private formBuilder: FormBuilder,
        private ipGroupApiService: IpGroupApiService,
        private logService: LogService,
        private networkingApiService: NetworkingApiService,
        private networkingService: NetworkingService,
        private notifyService: NotifyService,
        private router: Router,
        private transactionService: TransactionService,
        private utilsService: UtilsService
    ) {
        super();

        this.bondId = this.activatedRoute.snapshot.paramMap.get('interfaceId');

        this.setupFormGroups();
        this.setupFormSubscriber();
        this.setUpPageData();
    }

    /**
     * Get IP groups form fields.
     */
    get getGroupsFields() {
        return this.formGroup.get('groups') as FormArray;
    }

    /**
     * Get VLAN fields.
     */
    get getVlansFields() {
        return this.formGroup.get('vlans') as FormArray;
    }

    /**
     * Go back a step in the process.
     */
    public backStep(): void {
        this.activeStep--;
    }

    /**
     * Go forward a step in the process.
     */
    public nextStep(): void {
        this.activeStep++;
    }

    /**
     * Check if the button is disabled based on the form step state.
     */
    public isNextButtonDisabled(): boolean {
        const activeFormGroup = Object.keys(this.formGroup.controls)[this.activeStep - 1];

        return this.formGroup.controls[activeFormGroup].invalid;
    }

    /**
     * Convert control to type FormGroup.
     * @param control
     */
    public getFormGroup(control: any): FormGroup {
        return control as FormGroup;
    }

    /**
     * Helper function to make changes to the view when the dropdown changes.
     * @param formGroupName
     */
    public updateView(formGroupName: string): void {
        this.formGroup.get(formGroupName).markAsDirty();
    }

    /**
     * Remove multiple interfaces from a bond.
     * @private
     */
    public submit(): Observable<any> {
        return new Observable((observer) => {
            this.notifyService.muteLocalNotifications();
            this.transactionService.startTransaction()
                .pipe(
                    concatMap(() => this.removeInterfacesFromBond()),
                    concatMap(() => this.reassignVLANs()),
                    concatMap(() => this.reassignIPGroups()),
                    concatMap(() => this.removeBond()),
                    concatMap(() => this.networkingService.commitTransaction()),
                    takeUntil(this.destroyed$)
                ).subscribe({
                next: (): void => {
                    this.notifyService.unmuteLocalNotifications();
                    this.notifyService.success('Bond removed');
                    this.router.navigate(['../../'], { relativeTo: this.activatedRoute }).then().catch();
                    observer.next();
                    observer.complete();
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('BreakBondComponent:submit() error', error.message);
                    this.networkingService.submitFailed();
                    observer.error(error);
                    observer.complete();
                }
            });
        });
    }

    /**
     * Remove interfaces from the bond before breaking.
     * @private
     */
    private removeInterfacesFromBond(): Observable<any> {
        return new Observable((observer) => {
            const interfaces: any[] = this.networkingService.getBondInterfaces(this.nonVLANInterfaces, this.bondId);
            const interfaceLinks = from(interfaces || []);

            interfaceLinks.pipe(
                mergeMap(row => this.networkingApiService.removeInterfaceFromBond(row.id).pipe(catchError(error => of(error)))),
                takeUntil(this.destroyed$)
            ).subscribe({
                next: (): void => {
                    observer.next();
                    observer.complete();
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('BreakBondComponent:removeInterfacesFromBond() error', error.message);
                    observer.error(error);
                    observer.complete();
                }
            });
        });
    }

    /**
     * Re-assign all VLANs to other interfaces.
     * @private
     */
    private reassignVLANs(): Observable<any> {
        return new Observable((observer) => {
            if (!this.hasVLANs) {
                observer.next(true);
                observer.complete();
            } else {
                const vlanList = from(this.formattedVLANs || []);

                vlanList.pipe(
                    mergeMap(vlan => this.networkingApiService.editVLAN(vlan.id, vlan).pipe(catchError(error => of(error)))),
                    takeUntil(this.destroyed$)
                ).subscribe({
                    next: (): void => {
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('BreakBondComponent:reassignVLANs() error', error.message);
                        observer.error(error);
                        observer.complete();
                    }
                });
            }
        });
    }

    /**
     * Re-assign the IP groups before breaking the bond.
     * @private
     */
    private reassignIPGroups(): Observable<any> {
        return new Observable((observer) => {
            if (!this.hasGroups) {
                observer.next(true);
                observer.complete();
            } else {
                const ipGroupList = from(this.formattedIPGroups || []);
                ipGroupList.pipe(
                    mergeMap(ipGroup => this.editIPGroup(ipGroup)),
                    mergeMap(({ ipGroup, response }) => this.assignGroupInterface(response, ipGroup.interfaces)),
                    takeUntil(this.destroyed$)
                ).subscribe({
                    next: (): void => {
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('BreakBondComponent:reassignIPGroups() error', error.message);
                        observer.error(error);
                        observer.complete();
                    }
                });
            }
        });
    }

    /**
     * Edit the ip group before re-assigning interface.
     * @param ipGroup
     * @private
     */
    private editIPGroup(ipGroup: any): Observable<any> {
        return new Observable((observer) => {
            this.ipGroupApiService.editGroup(ipGroup)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (response: any): void => {
                        observer.next({ ipGroup, response });
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('BreakBondComponent:editIPGroup() error', error.message);
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Assign interface to group.
     * @param group
     * @param interfaces
     * @private
     */
    private assignGroupInterface(group: any, interfaces: any): Observable<any> {
        return new Observable((observer) => {
            this.networkingApiService.assignGroupInterface(interfaces, group.id)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('BreakBondComponent:assignGroupInterface() error', error.message);
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Delete the bond.
     * @private
     */
    private removeBond(): Observable<any> {
        return new Observable((observer) => {
            this.networkingApiService.removeBond(this.bondId)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: () => {
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel) => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Set up page data ready for editing.
     * @private
     */
    private setUpPageData(): void {
        const apiCalls = {
            interfaces: this.networkingApiService.getInterfaces(),
            groups: this.ipGroupApiService.getGroups(),
        };

        forkJoin(apiCalls)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: any): void => {
                    this.ipGroups = data['groups'];
                    this.formatPageData(data);
                    this.setVLANsForBond(data);
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('BreakBondComponent:setUpPageData() error', error.message);
                }
            });
    }

    /**
     * Format Page Data
     * @param data
     * @private
     */
    private formatPageData(data: any): void {
        const bond = data['interfaces'].find((item: any) => item.id === this.bondId);
        this.nonVLANInterfaces = data['interfaces'].filter((item: any) => item.type !== 'bond' && item.type !== 'vlan');
        const vlans = data['interfaces'].filter((item: any) => item.type === 'vlan' && item.settings.parentId === this.bondId);
        this.hasGroups = !!bond.groups.length;
        this.hasVLANs = !!vlans.length;

        this.dropdownContent = this.setDropdownContent(this.nonVLANInterfaces);

        this.createGroupsFormControls(bond, data['groups']);
        this.createVlanFormControls(vlans);

        if (!this.hasGroups && this.hasVLANs) {
            this.activeStep = 2;
        }

        this.isLoaded = true;
    }

    /**
     * Set VLAN
     * @param data
     * @private
     */
    private setVLANsForBond(data: any): void {
        this.bondVLANs = data['interfaces'].filter((item: {
            type: string;
            settings: { parentId: any; };
        }) => item.type === 'vlan' && item.settings.parentId === this.bondId);
    }

    /**
     * Set up dropdown content.
     * @param data
     * @private
     */
    private setDropdownContent(data: any): any[] {
        return this.utilsService.convertArrayToDropDown(data, 'id', 'name');
    }

    /**
     * Creates a form controller for each path in the template in edit mode
     * @param bond
     * @param groupsDetails
     * @private
     */
    private createGroupsFormControls(bond: any, groupsDetails: any[]): void {
        const bondGroups = bond.groups;
        for (let i = 0; i < bondGroups.length; i++) {
            const group = groupsDetails.find(item => item.id === bondGroups[i]);
            this.getGroupsFields.push(this.formBuilder.group({
                groupId: group.id,
                name: group.name,
                interfaceId: [null, Validators.required]
            }));
        }
    }

    /**
     * Creates a form controller for each path in the template in edit mode
     * @param vlans
     * @private
     */
    private createVlanFormControls(vlans: any[]): void {
        for (let i = 0; i < vlans.length; i++) {
            this.getVlansFields.push(this.formBuilder.group({
                vlanId: vlans[i].id,
                name: vlans[i].name,
                parentId: [null, Validators.required]
            }));
        }
    }

    /**
     * Setup Floating IP form.
     * @private
     */
    private setupFormGroups(): void {
        this.formGroup = this.formBuilder.group({
            groups: new FormArray([]),
            vlans: new FormArray([]),
        });
    }

    /**
     * Set up form subscribers.
     * @private
     */
    private setupFormSubscriber(): void {
        this.formGroup.get('vlans').valueChanges
            .subscribe(
                form => {
                    this.formattedVLANs = this.formatVlanData(form);
                }
            );

        this.formGroup.get('groups').valueChanges
            .subscribe(
                groups => {
                    this.formattedIPGroups = this.formatIPGroupData(groups);
                }
            );
    }

    /**
     * Format vlan data
     * @param vlans
     * @private
     */
    private formatVlanData(vlans: any[]): any[] {
        const formattedVlans: any[] = [];
        for (const vlan of vlans) {
            const foundvlan = this.bondVLANs.find(item => item.name === vlan.name);
            const formattedVlan = this.getFormattedVLANs(foundvlan, vlan);
            formattedVlans.push(formattedVlan);
        }

        return formattedVlans;
    }

    /**
     * Format the VLANs into the appropriate structure in order to edit.
     * @param vlanMatch
     * @param vlan
     * @private
     */
    private getFormattedVLANs(vlanMatch: any, vlan: any): any {
        return {
            id: vlanMatch.id,
            name: vlanMatch.name,
            type: 'vlan',
            vlanId: vlanMatch.settings.vlanId,
            parentId: vlan.parentId,
        };
    }

    /**
     * Format ip group data.
     * @param groups
     * @private
     */
    private formatIPGroupData(groups: any[]): any[] {
        const formattedGroups: any[] = [];
        for (const group of groups) {
            const foundGroup = this.ipGroups.find(item => item.name === group.name);
            const formattedGroup = this.formatIPGroup(foundGroup, group);
            formattedGroups.push(formattedGroup);
        }

        return formattedGroups;
    }

    /**
     * Format the IP group.
     * @param ipGroup
     * @param form
     * @private
     */
    private formatIPGroup(ipGroup: any, form: any): any {
        return {
            id: ipGroup.id,
            name: ipGroup.name,
            ipv4Mask: ipGroup.ipv4Mask,
            ipv4Network: ipGroup.ipv4Network,
            ipv6Mask: ipGroup.ipv6Mask,
            ipv6Network: ipGroup.ipv6Network,
            interfaces: form.interfaceId
        };
    }
}
